import { Badge } from "@/components/ui/badge";
import { POSITION_BG, MATURITY_BG } from "@/lib/constants";
import { ExternalLink, MapPin, Stethoscope, Quote, Clock3, ShieldCheck } from "lucide-react";

export const InsightCard = ({ insight, onOpen }) => (
  <article
    data-testid={`insight-card-${insight.id}`}
    onClick={() => onOpen(insight)}
    className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm shadow-slate-100 hover:border-teal-400 hover:shadow-md transition-all duration-200 cursor-pointer group"
  >
    {/* Layer 1 — position tags */}
    <div className="flex flex-wrap items-center gap-1.5 mb-3">
      <Badge variant="outline" className={`text-[10px] font-semibold ${POSITION_BG[insight.position]}`}>{insight.position}</Badge>
      <Badge variant="outline" className={`text-[10px] font-semibold ${MATURITY_BG[insight.maturity]}`}>{insight.maturity}</Badge>
      <span className="ml-auto flex items-center gap-2">
        <span className="text-[9px] font-mono uppercase tracking-wide text-amber-600/70" data-testid={`illustrative-tag-${insight.id}`}>Illustrative</span>
        <span className="font-mono text-[10px] text-slate-400">{insight.date}</span>
      </span>
    </div>
    {/* Layer 2 — core statement (paraphrases are never styled as quotations) */}
    {insight.statement_type === "AI-generated paraphrase" ? (
      <div className="border border-rose-200 bg-rose-50/50 rounded-lg px-3.5 py-2.5" data-testid={`paraphrase-block-${insight.id}`}>
        <p className="font-mono text-[8.5px] uppercase tracking-widest text-rose-600 mb-1">AI-generated paraphrase — not a verbatim quotation</p>
        <p className="text-sm text-slate-700 leading-relaxed">{insight.quote}</p>
      </div>
    ) : insight.governance?.quoted_use_blocked ? (
      <div className="border border-amber-300 bg-amber-50/60 rounded-lg px-3.5 py-2.5" data-testid={`quote-blocked-${insight.id}`}>
        <p className="font-mono text-[8.5px] uppercase tracking-widest text-amber-700 mb-1">Quoted use blocked — governance minimum incomplete · summarised position</p>
        <p className="text-sm text-slate-700 leading-relaxed">{insight.quote}</p>
        <p className="text-[10px] text-amber-700 mt-1.5 leading-snug">{insight.governance.quoted_use_note}</p>
      </div>
    ) : (
      <div className="flex gap-2.5">
        <Quote className="w-4 h-4 text-teal-500 shrink-0 mt-1" />
        <p className="text-sm text-slate-800 leading-relaxed font-medium">{insight.quote}</p>
      </div>
    )}
    {/* Layer 3 — speaker metadata */}
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-xs text-slate-500">
      <span className="flex items-center gap-1 font-medium text-slate-600">
        <Stethoscope className="w-3.5 h-3.5" />
        {insight.attribution?.hcp_name
          ? `${insight.attribution.hcp_name} — ${insight.attribution.institution}`
          : `${insight.hcp_role} · ${insight.practice_setting}`}
      </span>
      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{insight.country} · {insight.treatment_line}</span>
      <span className="text-[11px] text-slate-400">Subtheme: <span className="text-slate-600 font-medium">{insight.subtheme}</span></span>
    </div>
    {/* Layer 4 — source line + evidence links */}
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-[11px] text-slate-500 leading-snug" data-testid={`source-line-${insight.id}`}>
        <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mr-1.5">Source</span>
        {insight.source_type}: {insight.source_name}
        <span className="text-slate-300 mx-1.5">|</span>{insight.statement_type === "Direct quotation" ? "Direct quote" : insight.statement_type}
        <span className="text-slate-300 mx-1.5">|</span>{insight.attribution?.hcp_name ? "Named · verifiable public source" : insight.attribution?.withheld ? "Attribution withheld" : "Anonymised"}
        {insight.access_class && <><span className="text-slate-300 mx-1.5">|</span>{insight.access_class}</>}
        <span className="text-slate-300 mx-1.5">|</span>
        {insight.governance?.verification_status === "Verified" ? (
          <span className="inline-flex items-center gap-1 text-teal-600" data-testid={`verification-${insight.id}`}><ShieldCheck className="w-3 h-3" />Verified</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`verification-${insight.id}`} title="Visible in the review queue — cannot support approved findings"><Clock3 className="w-3 h-3" />Pending verification — review queue</span>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {insight.evidence_refs.map((r) => (
          <a
            key={r.label}
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`evidence-link-${insight.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-0.5 hover:bg-indigo-100 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />{r.label}
          </a>
        ))}
      </div>
    </div>
  </article>
);
